import { useEffect, useRef, useState } from 'react';

export function formatSearchTime(value) {
  const seconds = Math.max(0, Math.floor(Number(value) || 0));
  return [Math.floor(seconds / 3600), Math.floor(seconds % 3600 / 60), seconds % 60].map((part) => String(part).padStart(2, '0')).join(':');
}

export default function useSearchTimer({ identity, initialSeconds = 0, enabled, running = true, save, saveProgress }) {
  const [elapsed, setElapsed] = useState(0);
  const latest = useRef({ save, saveProgress, running });
  latest.current = { save, saveProgress, running };
  const clock = useRef({ seconds: 0, started: null });
  useEffect(() => {
    if (!enabled) return undefined;
    clock.current = { seconds: Number(initialSeconds) || 0, started: running && !document.hidden ? Date.now() : null };
    setElapsed(clock.current.seconds);
    const sample = () => clock.current.seconds + (clock.current.started ? Math.floor((Date.now() - clock.current.started) / 1000) : 0);
    const capturedSave = latest.current.save;
    const capturedProgress = latest.current.saveProgress;
    let viewSent = false;
    let viewTimeout;
    const scheduleView = () => {
      clearTimeout(viewTimeout);
      if (!document.hidden && !viewSent) viewTimeout = setTimeout(() => {
        viewSent = true;
        Promise.resolve(capturedSave?.(3, true)).catch(() => {});
      }, 3000);
    };
    const visibility = () => {
      clock.current.seconds = sample();
      clock.current.started = latest.current.running && !document.hidden ? Date.now() : null;
      if (document.hidden) {
        clearTimeout(viewTimeout);
        Promise.resolve(capturedSave?.(clock.current.seconds, false)).catch(() => {});
        Promise.resolve(capturedProgress?.()).catch(() => {});
      } else scheduleView();
    };
    scheduleView();
    document.addEventListener('visibilitychange', visibility);
    const interval = setInterval(() => setElapsed(sample()), 1000);
    return () => {
      clearTimeout(viewTimeout);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', visibility);
      Promise.resolve(capturedSave?.(sample(), false)).catch(() => {});
      Promise.resolve(capturedProgress?.()).catch(() => {});
    };
    // Identity deliberately controls the lifetime; response refetches must not
    // recreate timers or send duplicate view events for the same content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity, enabled]);
  useEffect(() => {
    if (!running && clock.current.started) {
      clock.current.seconds += Math.floor((Date.now() - clock.current.started) / 1000);
      clock.current.started = null;
      setElapsed(clock.current.seconds);
    }
  }, [running]);
  return elapsed;
}
